import {useTheme} from '@mui/material';
import React, {useState} from 'react';

import {NetworkTypeEnum} from 'domain/usecases';
import {getAddressforMaps} from 'presentation/utils';

import {
	ExamBookingEnum,
	FeaturesEnum,
	HealthBookingEnum,
	HealthGuideServicesTypeEnum,
} from 'presentation/constants';
import {useContractStatus} from 'presentation/hooks';
import {useServicePermission} from 'presentation/hooks/useServicePermission';
import {FlowEnum} from 'presentation/modules/booking/shared/constants/flowEnum';
import {IdentifierTelehealthEnum} from 'presentation/modules/guide/health/ui/pages/HealthGuide/types';
import {BookingTypeEnum, ufNameMap} from 'presentation/pages/private/booking/constants';
import {useBookingConfirmationDrawer} from 'presentation/pages/private/booking/hooks/useBookingConfirmationDrawer';
import {useFeatureFlag} from 'presentation/hooks/useFeatureFlag';
import {
	useAccountInformation,
	useMediaQueries,
	useQueryParams,
} from 'presentation/pages/private/hooks';
import {ServicesTypesEnum} from 'presentation/pages/private/services/types';
import {UseHealthGuideCardProps} from './types';
import {ServiceTypeEnum} from '../../types';
import {useBookingFlowStepsStore} from 'presentation/modules/booking/shared/store/useBookingFlowStepsStore';
import {useBookingStore} from 'presentation/modules/booking/shared/store/useBookingStore';
import {getHealthStepPosition} from 'presentation/modules/booking/health/utils/getHealthStepPosition';
import {useGetHomeQuery} from 'presentation/pages/private/home/query/useGetHomeQuery';
import {GetHealthBookingBySpecialtyParams} from 'presentation/modules/booking/health/ui/pages/HealthBooking/steps/HealthSpecialtySelection/components/HealthSpecialtiesList/hooks/useHealthSpecialtiesSelection/types';
import {ParsedHealthAppointment} from 'presentation/pages/private/home/utils/parseHealthAppointments/types';
import {useGetHealthSpecialtyAuthorizationMutation} from 'presentation/modules/booking/shared/mutations/useGetHealthSpecialtyAuthorizationMutation';
import {useNavigate} from 'react-router-dom';
import {useGetHealthSpecialtiesQuery} from 'presentation/modules/booking/shared/queries/useGetHealthSpecialtiesQuery';
import {useCongenereEligibilityStore} from 'presentation/pages/private/store/useCongenereEligibilityStore';

export function useHealthGuideCard({provider, showTradeName}: UseHealthGuideCardProps) {
	const theme = useTheme();
	const navigate = useNavigate();
	const [openConfirmationDrawer, setOpenConfirmationDrawer] = useState(false);
	const [isPending, setIsPending] = useState(false);
	const {SEARCH_PARAMS} = useQueryParams();
	const {
		serviceType,
		comingFrom,
		cityName,
		coordinates,
		ufCode,
		ufName,
		specialtyCode,
		specialtyName,
		subSpecialtyCode,
		hasSubSpecialty,
	} = SEARCH_PARAMS;

	const hasSub = Boolean(hasSubSpecialty && hasSubSpecialty === 'true');

	const address = getAddressforMaps({
		address: provider?.logradouro,
		district: provider?.nmBairro,
		city: provider?.nmCidade,
		state: provider?.cdUf,
		zipCode: provider?.cep,
	});

	const handleOpenConfirmationDrawer = () => {
		setOpenConfirmationDrawer(true);
	};

	const handleCloseConfirmationDrawer = () => {
		setOpenConfirmationDrawer(false);
	};

	const setActiveBookingFlowStep = useBookingFlowStepsStore(state => state.setBookingStep);
	const setBookingData = useBookingStore(state => state.setBookingData);
	const {SPECIALITY_OR_EXAM_STEP, PROFESSIONAL_STEP} = getHealthStepPosition({
		isManualLocation: true,
	});

	const configSpecialtyOrExamStep = () => {
		setActiveBookingFlowStep(SPECIALITY_OR_EXAM_STEP);
		setBookingData(prev => ({
			...prev,
			ufCode,
			ufName,
			cityName,
			specialtyName,
			isBookingByMedicalGuideFlow: true,
		}));
	};

	const configProfessionalStep = () => {
		setActiveBookingFlowStep(PROFESSIONAL_STEP);
		setBookingData(prev => ({
			...prev,
			ufCode: provider?.cdUf,
			ufName: ufNameMap[provider?.cdUf?.toUpperCase()],
			cityName: provider?.nmCidade,
			specialtyCode,
			specialtyName,
			subSpecialtyCode,
			unitCode: provider?.codigo ? String(provider.codigo) : '',
			unitName: provider?.nmPrestador ?? '',
			unitAddress: address?.formattedAddress ?? '',
			isBookingByMedicalGuideFlow: true,
		}));
	};

	const getHealthBookingBySpecialty = ({
		specialtyCode,
		appointments,
	}: GetHealthBookingBySpecialtyParams) => {
		return appointments?.find(
			booking =>
				booking.specialtyCode === specialtyCode &&
				(booking.bookingType === BookingTypeEnum.HEALTH ||
					booking.bookingType === BookingTypeEnum.TELEHEALTH),
		) as ParsedHealthAppointment | undefined;
	};

	const {mutateAsync: verifyHealthAuthorization} = useGetHealthSpecialtyAuthorizationMutation();
	const {refetch: refetchAppointmentsList} = useGetHomeQuery(undefined, false);
	const {refetch: refetchHealthSpecialties} = useGetHealthSpecialtiesQuery({
		isTelemedicine: false,
		enabled: false,
	});

	const configHealthStatesBeforeNavigate = async () => {
		setBookingData(prev => ({
			...prev,
			ufCode,
			ufName,
			cityName,
			isBookingByMedicalGuideFlow: true,
		}));
		if (specialtyCode && !hasSub) {
			try {
				const {
					content: {specialtyAuthorization},
				} = await verifyHealthAuthorization({speciality: specialtyCode});
				const {data: updatedAppointmentsList} = await refetchAppointmentsList();
				const alreadyScheduled = getHealthBookingBySpecialty({
					specialtyCode,
					appointments: updatedAppointmentsList?.appointments,
				});
				const {data: updatedHealthSpecialties} = await refetchHealthSpecialties();
				const isSpecialtyAvailable = updatedHealthSpecialties?.some(category =>
					category.list.some(specialty => specialty.code === specialtyCode),
				);
				if (alreadyScheduled || !specialtyAuthorization || !isSpecialtyAvailable) {
					configSpecialtyOrExamStep();
				} else {
					configProfessionalStep();
				}
			} catch (error) {
				// eslint-disable-next-line no-console
				console.error(error);
				configSpecialtyOrExamStep();
			}
		} else {
			configSpecialtyOrExamStep();
		}
	};

	const configExamStatesBeforeNavigate = () => {
		configSpecialtyOrExamStep();
	};

	const [openPhoneDrawerState, setOpenPhoneDrawerState] = useState(false);
	const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
	const [showEmptyPhoneSnackbar, setShowEmptyPhoneSnackbar] = useState(false);
	const {isEnabled: isSchedulingByMedicalGuideEnabled} = useFeatureFlag(
		FeaturesEnum.PORTAL_FEATURE_SCHEDULING_BY_MEDICAL_GUIDE,
	);
	const {setSelectedServiceType, handleOpenBookingConfirmationDrawer} =
		useBookingConfirmationDrawer();
	const {canOpenService} = useServicePermission();
	const {isMobile} = useMediaQueries();
	const {isContractSuspended} = useContractStatus();
	const {isEarlyAccess} = useAccountInformation();
	const {congenereEligibility} = useCongenereEligibilityStore();

	const isTelemedicine =
		provider?.tipoRede === NetworkTypeEnum.SELF_NETWORK &&
		provider?.nmBairro === IdentifierTelehealthEnum.DIGITAL_TELECONSULTATION;

	const isAccreditedNetwork = provider?.tipoRede === NetworkTypeEnum.ACCREDITED_NETWORK;

	const isProviderAccreditedNetwork = provider?.tipoRede == null;

	const isComingFromExam =
		comingFrom === FlowEnum.EXAM &&
		serviceType === HealthGuideServicesTypeEnum.AUXILIARY_SERVICES;

	const shouldShowCallButton =
		isAccreditedNetwork || isProviderAccreditedNetwork || isComingFromExam;

	const shouldShowAppointmentButton =
		provider?.tipo === ServiceTypeEnum.OFFICES_CLINICS &&
		!isProviderAccreditedNetwork &&
		!isAccreditedNetwork &&
		!isContractSuspended &&
		!isEarlyAccess &&
		!congenereEligibility;

	const shouldShowExamButton =
		provider?.tipo === ServiceTypeEnum.SUPPORT_SERVICES &&
		!isProviderAccreditedNetwork &&
		!isAccreditedNetwork &&
		!isComingFromExam &&
		!isEarlyAccess &&
		!congenereEligibility;

	const phoneNumbers =
		provider?.nuTelefone
			?.split('/')
			.map(p => p.trim())
			.filter(p => p.length > 0) || [];

	const phoneNumbersLength = phoneNumbers?.length ?? 0;
	const isPhoneNumberEmpty = phoneNumbersLength === 0;
	const hasMoreThanOnePhoneNumber = phoneNumbersLength > 1;
	const shouldCallDirectlyToOnlyPhone = phoneNumbers && phoneNumbersLength === 1 && isMobile;
	const shouldOpenPhoneDrawer = hasMoreThanOnePhoneNumber && isMobile;
	const isPhoneNumberMenuOpen = Boolean(anchorEl);
	const showPhonesDrawer = isMobile && phoneNumbers && hasMoreThanOnePhoneNumber;
	const hasPhoneNumbers = phoneNumbers?.length > 0;

	const handleOpenPhoneMenu = (event: React.MouseEvent<HTMLButtonElement>) => {
		if (isPhoneNumberEmpty) {
			setShowEmptyPhoneSnackbar(true);
			return;
		}

		if (shouldCallDirectlyToOnlyPhone) {
			const phone = phoneNumbers[0];
			window.location.href = `tel:${phone}`;
			return;
		}

		if (shouldOpenPhoneDrawer) {
			setOpenPhoneDrawerState(true);
			return;
		}

		setAnchorEl(event.currentTarget);
	};

	const primaryName =
		showTradeName && provider?.nmFantasia
			? provider?.nmFantasia
			: provider?.nmLocal || provider?.nmPrestador || '-';

	const shouldShowQualifications = provider?.qualificacoes?.length > 0;

	const handleSelectAppointment = async (): Promise<boolean> => {
		if (isSchedulingByMedicalGuideEnabled) {
			await configHealthStatesBeforeNavigate();
		}

		const canAccessService = canOpenService(
			isTelemedicine ? ServicesTypesEnum.TELEHEALTH : ServicesTypesEnum.HEALTH,
		);

		if (!canAccessService) return false;

		if (isTelemedicine) {
			setSelectedServiceType(BookingTypeEnum.TELEHEALTH);
		} else {
			setSelectedServiceType(BookingTypeEnum.HEALTH);
		}

		if (!isSchedulingByMedicalGuideEnabled) {
			handleOpenBookingConfirmationDrawer();
		}

		return true;
	};

	const handleSelectExam = (): boolean => {
		const canAccessService = canOpenService(ServicesTypesEnum.EXAMS);

		if (!canAccessService) return false;

		setSelectedServiceType(BookingTypeEnum.EXAM);
		if (isSchedulingByMedicalGuideEnabled) {
			configExamStatesBeforeNavigate();
		} else {
			handleOpenBookingConfirmationDrawer();
		}

		return true;
	};

	const handleConfirmAndContinue = async () => {
		if (serviceType === HealthGuideServicesTypeEnum.AUXILIARY_SERVICES) {
			const success = handleSelectExam();
			if (!success) return;
			handleOpenConfirmationDrawer();
			return;
		}

		setIsPending(true);
		let canProceed = false;
		try {
			canProceed = await handleSelectAppointment();
		} finally {
			setIsPending(false);
		}

		if (!canProceed) return;

		handleOpenConfirmationDrawer();
	};

	const handleNavigateToBookingFlow = () => {
		if (serviceType === HealthGuideServicesTypeEnum.AUXILIARY_SERVICES) {
			navigate(ExamBookingEnum.EXAM_BOOKING_FLOW);
			return;
		}
		navigate(HealthBookingEnum.HEALTH_BOOKING_FLOW);
	};

	return {
		handleOpenPhoneMenu,
		primaryName,
		shouldShowQualifications,
		theme,
		address,
		shouldShowCallButton,
		shouldShowAppointmentButton,
		shouldShowExamButton,
		phoneNumbers,
		isPhoneNumberMenuOpen,
		setAnchorEl,
		showPhonesDrawer,
		openPhoneDrawerState,
		setOpenPhoneDrawerState,
		hasPhoneNumbers,
		handleSelectAppointment,
		handleSelectExam,
		showEmptyPhoneSnackbar,
		setShowEmptyPhoneSnackbar,
		cityName,
		coordinates,
		isTelemedicine,
		anchorEl,
		isPending,
		openConfirmationDrawer,
		handleCloseConfirmationDrawer,
		handleOpenConfirmationDrawer,
		handleConfirmAndContinue,
		isSchedulingByMedicalGuideEnabled,
		handleNavigateToBookingFlow,
	};
}
